import {
  DEFAULT_COMPANY_ADMIN_ID,
  DEFAULT_ROLES,
  isRoleId,
  type RoleData,
  type RoleId,
  type SmallUser,
  type TinyRole,
  type UserProfileData,
  type WithRoleId,
} from '../model'
import { isNotDefined } from './isEmpty'

export function toRoleId(role: WithRoleId): RoleId {
  return role.roleId
}

export function isAdminRole(role: Pick<RoleData, 'roleType'>): boolean {
  return role.roleType === 'admin'
}

export function isManagerRole(role: Pick<RoleData, 'roleType'>): boolean {
  return role.roleType === 'manager'
}

export const compareRoles = (a: RoleData, b: RoleData): number => {
  if (a.roleType !== b.roleType) {
    return getRoleSortScore(b) - getRoleSortScore(a)
  }
  if (a.name !== b.name) {
    return b.name.localeCompare(a.name)
  }
  return b.roleId.localeCompare(a.roleId)
}

const getRoleSortScore = (role: RoleData): number => {
  switch (role.roleType) {
    case 'admin':
      return 2
    case 'manager':
      return 1
    default:
      return 0
  }
}

export function toTinyRole(role: Pick<RoleData, 'roleId' | 'roleType' | 'name'>): TinyRole {
  return {
    roleId: role.roleId,
    roleType: role.roleType,
    name: role.name,
  }
}

export function isDefaultRoleId(roleId: unknown): roleId is RoleId {
  if (!isRoleId(roleId)) return false
  return DEFAULT_ROLES.some((role) => role.roleId === roleId)
}

export function isDefaultAdminRole(teamId: RoleId): boolean {
  return teamId === DEFAULT_COMPANY_ADMIN_ID
}

export function roleCanBeInTeamMember(role: Pick<RoleData, 'assignment' | 'roleType'>): boolean {
  if (isAdminRole(role)) return false
  if (isNotDefined(role.assignment)) return true
  return role.assignment === 'team'
}

export function visibleTeamMembershipRoles<T extends Pick<RoleData, 'roleId' | 'assignment' | 'roleType'>>(
  allRoles: T[],
  selectedRoleIds: RoleId[]
): T[] {
  return allRoles.filter((role) => roleCanBeInTeamMember(role) || selectedRoleIds.includes(role.roleId))
}

export function roleCanBeInRoleMember(role: Pick<RoleData, 'assignment' | 'roleType'>): boolean {
  if (isNotDefined(role.assignment)) return true
  return role.assignment === 'user'
}

export function isRecruitRole(role: Pick<RoleData, 'roleType'>): boolean {
  return role.roleType === 'recruit'
}

export function hasRecruitRole(user?: Pick<UserProfileData, 'roles'>): boolean {
  return user?.roles?.some(isRecruitRole) ?? false
}

export function userHasRole(user: Pick<SmallUser, 'roles'>, roleId: RoleId): boolean {
  return user.roles?.some((role) => role.roleId === roleId) ?? false
}
